<template>
    <b-container fluid>
        <b-card no-body class="shadow inbox-card-style">
            <b-card-header class="inbox-header">
                Inbox
            </b-card-header>
            <b-card-body>
                <b-container fluid>
                    <b-table
                        id="inboxTable"
                        primary-key="item_id"
                        borderless
                        hover
                        responsive
                        stacked="md"
                        caption-top
                        sort-by="status_tm"
                        sort-desc
                        :fields="inboxTableFields"
                        :items="inboxItemsDisplay"
                        @row-clicked="navigateToItem"
                    >
                        <template v-if="isReviewer" v-slot:cell(applicant)="row">
                            {{ row.item.applicant }}
                        </template>
                        <template v-slot:cell(itemTitle)="row" class="itemTitle">
                            <div class="itemTitle" :title="row.value" />
                        </template>
                        <template v-if="isApplicant" v-slot:cell(submit)="row">
                            <confirmable-submit-action-button
                                v-if="isSubmittable(row.item)"
                                :actionable-item="row"
                                :icon-config="{
                                    variant: 'submit',
                                    classes: ['fa-fw', 'fa-lg'],
                                }"
                                @submitConfirmed="onSubmitConfirm($event.item)"
                            >
                                <template slot="default">
                                    <p>
                                        By clicking
                                        <strong>SUBMIT</strong> below, this item will be marked as
                                        SUBMITTED and the Reviewer for your FICE will be notified.
                                    </p>
                                    <p>
                                        If you wish to make further edits to this item, click the
                                        <strong>CANCEL</strong> button below to return to the Inbox
                                        List.
                                    </p>
                                </template>
                            </confirmable-submit-action-button>
                        </template>
                        <template slot="table-caption">
                            Click on the desired item in the table to navigate to that item.
                        </template>
                    </b-table>
                </b-container>
            </b-card-body>
        </b-card>
    </b-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Arc } from '$Shared/@arc/vue-arc';

export default {
    name: 'Inbox',

    metaInfo() {
        return { title: 'Inbox' };
    },

    data() {
        return {
            form: {
                items: [],
            },
            submitButtonPopoverShow: false,
        };
    },

    computed: {
        ...mapGetters({
            isApplicant: 'auth/isApplicant',
            isReviewer: 'auth/isReviewer',
            isAuthorized: 'auth/isAuthorized',
            inboxItemsDisplay: 'inbox/inboxItemsDisplay',
        }),

        inboxTableFields() {
            let fields = [
                {
                    key: 'ay',
                    label: 'AY',
                    sortable: true,
                    thStyle: { width: '5ch' },
                },
                {
                    key: 'resource_id',
                    label: 'Item ID',
                    sortable: true,
                    thStyle: { width: '10ch' },
                },
                {
                    key: 'resourceTypeDisplay',
                    label: 'Type',
                    sortable: true,
                    thStyle: { width: '10%' },
                },
                {
                    key: 'itemTitle',
                    label: 'Item Title',
                    sortable: true,
                    thStyle: {
                        // DEFINED IN CSS BELOW
                    },
                    formatter: (value, key, item) => {
                        const trimLength = 50;
                        const title = `${item.itemTitle}`;
                        const trimmed = title && title.length > trimLength
                            ? title.substring(0, (trimLength - 3)) + '...'
                            : item.itemTitle;

                        return trimmed || 'No Activity Title';
                    }
                },
                {
                    key: 'status',
                    label: 'Status',
                    sortable: true,
                    thStyle: { width: '10%' },
                },
                {
                    key: 'status_tm',
                    label: 'Status Time',
                    sortable: true,
                    thStyle: { width: '10%' },
                },
            ];

            if (this.isApplicant) {
                let row = {
                    key: 'submit',
                    label: 'Submit',
                    sortable: true,
                    thStyle: { width: '8%', 'text-align': 'center' },
                };
                fields.push(row);
            } else if (this.isReviewer) {
                let row = {
                    key: 'applicant',
                    label: 'Applicant',
                    sortable: true,
                    thStyle: { width: '30%' },
                };
                fields.push(row);
            }

            return fields;
        },
    },

    async created() {
        await this.fetchInboxItems();
    },

    methods: {
        ...mapActions({
            fetchInboxItems: 'inbox/fetchInboxItems',
            submitCoverPage: 'inbox/submitCoverPage',
            submitProposal: 'inbox/submitProposal',
            submitLocalApplication: 'inbox/submitLocalApplication',
            submitPurchase: 'inbox/submitPurchase',
            submitEyprData: 'inbox/submitEyprData',
            submitEyprNarrative: 'inbox/submitEyprNarrative',
        }),

        isSubmittable(item) {
            return this.isAuthorized('submit', item.resource_type, item.status);
        },

        navigateToItem(item) {
            const type = Arc.Text.camelize(item.resource_type);
            const typeRoutes = ({ resource_id, application_id, relation_ids, ay }) => {
                return {
                    'localApplication': {
                        name: 'localApplication.History',
                        params: {
                            localApplicationAy: ay,
                            localApplicationId: resource_id,
                        },
                    },
                    'coverPage': {
                        name: 'coverpage.History',
                        params: {
                            applicationId: application_id,
                            coverPageId: resource_id
                        },
                    },
                    'proposal': {
                        name: 'proposal.History',
                        params: {
                            applicationId: application_id,
                            proposalId: resource_id,
                        }
                    },
                    'purchase': {
                        name: 'purchase.History',
                        params: {
                            purchaseId: resource_id,
                            applicationId: application_id,
                            proposalId: item.relation_ids ? item.relation_ids.proposal_id : null,
                            costId: item.relation_ids ? item.relation_ids.cost_id : null,
                        }
                    },
                    'eyprData': {
                        name: 'eyprData.History',
                        params: {
                            eyprDataId: resource_id,
                            applicantId: item.applicant_id,
                        }
                    },
                    'eyprNarrative': {
                        name: 'eyprNarrative.History',
                        params: {
                            eyprNarrativeId: resource_id,
                            applicantId: item.applicant_id,
                        }
                    },
                };
            };

            this.$router.push(typeRoutes(item)[type]);
        },

        onClose() {
            this.$root.$emit('bv::hide::popover');
        },

        onShow(targetID, item) {
            this.$root.$emit('bv::show::popover', targetID);
        },

        // TODO/Refactor/Normalize: Try and extract these resource_type names somewhere common and make them camelCased
        async onSubmitConfirm(item) {
            const type = Arc.Text.camelize(item.resource_type);
            if (type === 'coverPage') {
                await this.submitCoverPage({
                    applicationId: item.application_id,
                    coverPageId: item.resource_id,
                });
            } else if (type === 'proposal') {
                await this.submitProposal({
                    applicationId: item.application_id,
                    proposalId: item.resource_id,
                });
            } else if (type === 'purchase') {
                await this.submitPurchase({
                    purchaseId: item.resource_id,
                    applicationId: item.application_id,
                    proposalId: item.relation_ids.proposal_id,
                    costId: item.relation_ids.cost_id,
                });
            } else if (type === 'eyprData') {
                await this.submitEyprData({
                    eyprDataId: item.resource_id,
                });
            } else if (type === 'eyprNarrative') {
                await this.submitEyprNarrative({
                    eyprNarrativeId: item.resource_id,
                });
            } else if (type === 'localApplication') {
                await this.submitLocalApplication(item.resource_id);
            }

            this.$root.$emit('bv::hide::popover');
        },
    },
};
</script>

<style lang="scss">
@import '$StyleVars';

/* TODO: Lots of duplicated table styles here */
$submit-button-width: 41.31px;
$estimated-title-column-width: calc(
    75vw - (11ch + 8ch + 22ch + (#{$submit-button-width}) + (16px * 3))
);

.inbox-card-style {
    border: 0;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
}

.details-card-style {
    border: 0;
    background-color: #00000010;
}

.details-card-header {
    border: 0;
    color: #ffffff;
    background-color: #00000085;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
}

.inbox-header {
    color: #ffffff;
    text-transform: uppercase;
    font-size: 1.5rem;
    font-weight: bold;
    background-color: $realm-primary;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
}

.nav-item-status-icon {
    margin-top: -0.25rem;
    padding-left: 0.25rem;
    font-size: 1.25em;
    float: right;
}

.title-display {
    font-size: 18px;
    color: #fafafa;
    background-color: $realm-primary;
}

.title-display-label {
    font-weight: lighter;
    text-align: right;
}

.title-display-content {
    font-weight: bolder;
    text-align: left;
}

.shadow {
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

hr {
    border-top: 1px dotted #696969;
}

.cc-top-row-button {
    background: rgb(109, 140, 57);
    background: linear-gradient(180deg, rgba(109, 140, 57, 1) 0%, rgba(109, 140, 57, 0.75) 50%);
    border: 0;
}

tr {
    cursor: pointer;
}

.active-table-row {
    background-color: #dae2ce;
}

.rounded-button {
    border-radius: 5px;
    color: #ffffff;
    font-weight: bold;
    background-color: #006438;
    border: 0;
    box-shadow: 0 2px 5px gray;

    &:hover,
    &:focus {
        background-color: #00311c;
    }

    &:active {
        color: #ffffff;
        font-weight: bolder;
        border-radius: 5px 5px 0 0;

        background: rgb(109, 140, 57);
        background: linear-gradient(180deg, rgba(109, 140, 57, 1) 0%, rgba(109, 140, 57, 0.75) 50%);
    }
}

.cancel-button {
    color: #00000090;
}

.cancel-button:hover {
    color: #00000090;
    border-color: #7d7d7d;
    border-radius: 0.25rem;
    background-color: #00000015;
}

.page-link {
    margin-left: -1px;
    font-weight: bold;
    line-height: 1.25;
    color: #006438;
    border: 1px dotted gray;
}

.page-item.active .page-link {
    font-weight: bold;
    color: #fff;
    background-color: #006438;
    border: 1px solid white;
}

.table {
    tbody {
        td {
            .itemTitle {

                &:before {
                    content: attr(title);
                }
            }
        }
    }
}

.submit-button {
    color: $submit-button-color;

    &:hover {
        background-color: scale_color($submit-button-color, $alpha: 8%);
        border-color: $submit-button-color;
    }
}

.delete-button {
    color: $delete-button-color;

    &:hover {
        background-color: scale_color($delete-button-color, $alpha: 8%);
        border-color: $delete-button-color;
    }
}

.submit-button,
.delete-button {
    background-color: #ffffff00;
    border-color: #ffffff00;
    font-weight: bold;

    &:hover {
        border-radius: 0.25rem;
    }
}
</style>
